import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformSettingsAPI, apiClient } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';


interface TelegramSettingsForm {
  telegram_bot_token: string;
  telegram_session_expire_hours: number;
  telegram_max_pin_attempts: number;
  telegram_pin_lockout_minutes: number;
}

const TelegramSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tokenChanged, setTokenChanged] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['telegram-settings'],
    queryFn: () => platformSettingsAPI.getTelegramSettings(),
    select: (res) => res.data.data,
  });

  const { data: botStatus } = useQuery({
    queryKey: ['telegram-bot-status'],
    queryFn: async () => {
      const res = await apiClient.client.get('/telegram/status');
      return res.data as { status: string; webhook: boolean; bot_username: string | null };
    },
    refetchInterval: 30000,
  });

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<TelegramSettingsForm>({
    values: data ? {
      telegram_bot_token: '',
      telegram_session_expire_hours: data.telegram_session_expire_hours,
      telegram_max_pin_attempts: data.telegram_max_pin_attempts,
      telegram_pin_lockout_minutes: data.telegram_pin_lockout_minutes,
    } : undefined,
  });

  const mutation = useMutation({
    mutationFn: (formData: TelegramSettingsForm) => {
      const payload: Record<string, unknown> = {};
      if (formData.telegram_bot_token) {
        payload.telegram_bot_token = formData.telegram_bot_token;
      }
      payload.telegram_session_expire_hours = formData.telegram_session_expire_hours;
      payload.telegram_max_pin_attempts = formData.telegram_max_pin_attempts;
      payload.telegram_pin_lockout_minutes = formData.telegram_pin_lockout_minutes;
      return platformSettingsAPI.updateTelegramSettings(payload as any);
    },
    onSuccess: (res) => {
      const botRestarted = (res.data as any).bot_restarted;
      if (botRestarted) {
        toast.success('Impostazioni salvate e bot riavviato');
      } else {
        toast.success('Impostazioni salvate');
      }
      queryClient.invalidateQueries({ queryKey: ['telegram-settings'] });
      queryClient.invalidateQueries({ queryKey: ['telegram-bot-status'] });
      setTokenChanged(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const restartMutation = useMutation({
    mutationFn: () => platformSettingsAPI.restartTelegramBot(),
    onSuccess: (res) => {
      const username = res.data.bot_username;
      toast.success(username ? `Bot riavviato (@${username})` : 'Bot riavviato');
      queryClient.invalidateQueries({ queryKey: ['telegram-bot-status'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel riavvio del bot');
    },
  });

  if (!user?.is_host) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Accesso negato. Solo gli utenti host possono accedere a questa pagina.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni Telegram Bot</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configura il token e i parametri del bot Telegram.
        </p>
      </div>

      {data && (
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            data.source === 'database'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            Fonte: {data.source === 'database' ? 'Database' : 'Variabili d\'ambiente'}
          </span>
          {data.telegram_bot_token_set && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Token configurato
            </span>
          )}
          {botStatus && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              botStatus.status === 'running'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {botStatus.status === 'running' ? 'Bot connesso' : 'Bot non attivo'}
              {botStatus.bot_username && ` (@${botStatus.bot_username})`}
            </span>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="bg-white shadow rounded-lg">
        <div className="p-6 space-y-6">
          {/* Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bot Token
            </label>
            <input
              type="password"
              {...register('telegram_bot_token')}
              onChange={(e) => {
                register('telegram_bot_token').onChange(e);
                if (e.target.value) setTokenChanged(true);
                else setTokenChanged(false);
              }}
              placeholder={data?.telegram_bot_token_set ? '(token configurato - lascia vuoto per non modificare)' : 'Inserisci il token del bot'}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lascia vuoto per mantenere il token attuale.
            </p>
          </div>

          {tokenChanged && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-700">
                Il bot verra riavviato automaticamente con il nuovo token al salvataggio.
              </p>
            </div>
          )}

          {/* Session Expire Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scadenza sessione (ore)
            </label>
            <input
              type="number"
              {...register('telegram_session_expire_hours', {
                valueAsNumber: true,
                min: { value: 1, message: 'Minimo 1 ora' },
                max: { value: 720, message: 'Massimo 720 ore (30 giorni)' },
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.telegram_session_expire_hours && (
              <p className="mt-1 text-sm text-red-600">{errors.telegram_session_expire_hours.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Dopo quante ore la sessione Telegram scade automaticamente.</p>
          </div>

          {/* Max PIN Attempts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tentativi PIN massimi
            </label>
            <input
              type="number"
              {...register('telegram_max_pin_attempts', {
                valueAsNumber: true,
                min: { value: 1, message: 'Minimo 1' },
                max: { value: 20, message: 'Massimo 20' },
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.telegram_max_pin_attempts && (
              <p className="mt-1 text-sm text-red-600">{errors.telegram_max_pin_attempts.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Numero massimo di tentativi PIN prima del blocco.</p>
          </div>

          {/* Lockout Minutes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durata blocco PIN (minuti)
            </label>
            <input
              type="number"
              {...register('telegram_pin_lockout_minutes', {
                valueAsNumber: true,
                min: { value: 1, message: 'Minimo 1 minuto' },
                max: { value: 1440, message: 'Massimo 1440 minuti (24 ore)' },
              })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.telegram_pin_lockout_minutes && (
              <p className="mt-1 text-sm text-red-600">{errors.telegram_pin_lockout_minutes.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Minuti di blocco dopo aver superato i tentativi PIN massimi.</p>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-3 flex justify-between rounded-b-lg">
          <button
            type="button"
            onClick={() => restartMutation.mutate()}
            disabled={restartMutation.isPending || !data?.telegram_bot_token_set}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {restartMutation.isPending ? 'Riavvio...' : 'Riavvia Bot'}
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || (!isDirty && !tokenChanged)}
            className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Salvataggio...' : 'Salva impostazioni'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TelegramSettings;
